import { HttpParams } from '@angular/common/http'
import { Component, OnDestroy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjChartModule } from '@grapecity/wijmo.angular2.chart'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellType, DataMap, GridPanel } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, FeatureType, FuelEntry, Grid, ListItem, ModalSize, TimeUnit } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridColumnGroupComponent } from '../../shared/reusable-components/grid-column-group/grid-column-group.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { FuelRecordComponent } from '../fuel-records/fuel-record.component'
import { BurnRateComponent } from './components/burn-rate.component'
import { HoursVarianceComponent } from './components/hours-variance.component'
import { LifetimeMeterComponent } from './components/lifetime-meter.component'

@Component({
    templateUrl: './fuel-analytics.component.html',
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavContent,
        WjChartModule,
        AutoSizeDirective,
        FormsModule,
        ActionToolbarCount,
        GridExportComponent,
        WjInputModule,
        GridColumnGroupComponent,
        WjGridModule,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        LifetimeMeterComponent,
        HoursVarianceComponent,
        BurnRateComponent,
        NgbNavOutlet,
    ],
})
export class FuelAnalyticsComponent extends BaseComponent implements OnDestroy {
    faSearch = faSearch
    active = 0
    grid: Grid<FuelEntry>
    totalsGrid: Grid<any>
    rateGrid: Grid<any>
    negHoursGrid: Grid<any>
    tooHighRateGrid: Grid<any>
    noHoursGrid: Grid<any>
    gpsHoursGrid: Grid<any>
    transportsGrid: Grid<any>
    transportsTotalsGrid: Grid<any>
    startDate = new Date(2023, 1, 1)
    endDate = new Date()
    truckChartData!: any

    timeUnitOptions = [
        new ListItem(TimeUnit.Day, 'Day'),
        new ListItem(TimeUnit.Week, 'Week'),
        new ListItem(TimeUnit.Month, 'Month'),
        new ListItem(TimeUnit.Quarter, 'Quarter'),
        new ListItem(TimeUnit.Year, 'Year'),
    ]
    selectedTimeUnit = TimeUnit.Day

    allEquipment: any[] = []
    allJobs: any[] = []

    gpsResultMap = new DataMap(
        [
            { id: 0, name: 'GPS and Fuel Records Match With 48 Hours' },
            { id: 1, name: 'Mismatched Fuel Record and GPS Hours' },
            { id: 2, name: 'No GPS For Comparison' },
        ],
        'id',
        'name',
    )

    constructor() {
        super()
        this.grid = new Grid<FuelEntry>(this.services)
            .setupModalEditing(
                this.services.data.fuelEntries,
                'Fuel Record',
                FuelRecordComponent,
                () => {
                    const fuelEntry = new FuelEntry()
                    FuelEntry.addTableProps(fuelEntry)
                    return fuelEntry
                },
                this,
                ModalSize.medium,
            )
            .wrapColumnHeaders()
            .filterBy(item => [item.vehicle, item.source, item.userName])

        this.grid.itemEdited.on(this.updatePropsOnEdit.bind(this))

        this.totalsGrid = new Grid<any>(this.services)
        this.rateGrid = new Grid<any>(this.services).wrapColumnHeaders()
        this.negHoursGrid = new Grid<any>(this.services).wrapColumnHeaders()
        this.tooHighRateGrid = new Grid<any>(this.services).wrapColumnHeaders()
        this.noHoursGrid = new Grid<any>(this.services).wrapColumnHeaders()
        this.gpsHoursGrid = new Grid<any>(this.services).wrapColumnHeaders()
        this.transportsGrid = new Grid<any>(this.services).wrapColumnHeaders()
        this.transportsTotalsGrid = new Grid<any>(this.services).wrapColumnHeaders()
        this.refresh()
    }
    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.post('/api/FuelAnalytics/GetAnalytics', { startDate: this.startDate, endDate: this.endDate }),
                    this.services.data.post('/api/FuelAnalytics/GetRateAnalytics', { startDate: this.startDate, endDate: this.endDate }),
                    this.services.data.fuelEntries.queryAllByAction('GetForValidation'),
                    this.services.data.get('api/VPEquipment'),
                    this.services.data.vpJobs.queryAll(),
                    this.services.data.fuelRecordDefaults.queryAll(),
                    this.services.data.get('/api/VpData/QueryFuelPrices'),
                    this.services.data.get('/api/FuelAnalytics/GetHourGpsValidation'),
                    this.services.data.get('/api/FuelAnalytics/GetTransporterMetrics'),
                    this.services.data.get('/api/FuelAnalytics/GetFuelChart'),
                    this.services.data.get('/api/FuelAnalytics/GetTransportData', new HttpParams().set('timeUnit', this.selectedTimeUnit)),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.totalsGrid.items = result[0]
                        this.rateGrid.items = result[1].rates
                        this.negHoursGrid.items = result[1].validationNegHours
                        this.tooHighRateGrid.items = result[1].validationTooHighRate
                        this.noHoursGrid.items = result[1].validationNoHourOrGallons
                        this.gpsHoursGrid.items = result[7]
                        this.transportsGrid.items = result[8]
                        this.transportsTotalsGrid.items = result[10]

                        this.grid.items = this.services.data.fuelEntries.items
                        this.allEquipment = result[3]
                        this.allJobs = result[4]
                        this.truckChartData = result[9]
                        console.log(this.truckChartData)
                    },
                }),
        )
    }
    itemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            cell.classList.remove('yellow-cell')
        }
    }
    tabChanged() {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 50)
    }
    updatePropsOnEdit(args) {
        FuelEntry.addTableProps(args.newItem)
    }
    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.EquipmentFuelRecordsEdit])
    }
}
